/**
 * Single source of truth: loads evaluation-rules.yaml (this package) once.
 * Rule-doc parameter bundles live under ruleVersions.current | legacy.
 *
 * Vite client/SSR bundles use __INLINE_EVALUATION_RULES_YAML__ (build-config/vite.config.ts).
 * server.js and tsx scripts set the same value on globalThis before posture modules load.
 */

import yaml from 'js-yaml';

declare const __INLINE_EVALUATION_RULES_YAML__: string | undefined;

export type EvaluationRulesRoot = Record<string, unknown> & {
  selfAssessment?: {
    savePictures?: boolean;
  };
  ruleVersions?: {
    current?: Record<string, unknown>;
    legacy?: Record<string, unknown>;
  };
};

let cachedRules: EvaluationRulesRoot | null = null;

function resolveInlineEvaluationRulesYaml(): string | undefined {
  if (typeof __INLINE_EVALUATION_RULES_YAML__ === 'string') {
    return __INLINE_EVALUATION_RULES_YAML__;
  }

  const globalInline = (globalThis as { __INLINE_EVALUATION_RULES_YAML__?: string })
    .__INLINE_EVALUATION_RULES_YAML__;
  if (typeof globalInline === 'string') {
    return globalInline;
  }

  return undefined;
}

function loadEvaluationRulesYamlText(): string {
  const inlineYaml = resolveInlineEvaluationRulesYaml();
  if (inlineYaml) {
    return inlineYaml;
  }

  const mode = import.meta.env?.MODE;
  const isBrowserClient =
    typeof window !== 'undefined' && mode !== 'test' && !import.meta.env?.SSR;

  if (isBrowserClient) {
    throw new Error(
      'Evaluation rules YAML is not inlined for this client build. Check vite define.',
    );
  }

  throw new Error(
    'Evaluation rules YAML is not available. Set globalThis.__INLINE_EVALUATION_RULES_YAML__ (server.js / scripts/lib/bootstrap-inline-evaluation-rules.ts) or vite/vitest define.',
  );
}

export function getEvaluationRules(): EvaluationRulesRoot {
  if (!cachedRules) {
    cachedRules = yaml.load(loadEvaluationRulesYamlText()) as EvaluationRulesRoot;
  }
  return cachedRules;
}

export function invalidateEvaluationRulesCache(): void {
  cachedRules = null;
}

/**
 * Raw doc-keyed bundles from YAML (APT_P1, SB_P1, P2, P3, AOPosture, SBPosture).
 */
export function getRuleVersionDocBundle(version: 'current' | 'legacy'): Record<string, unknown> {
  const root = getEvaluationRules();
  const bundle = root.ruleVersions?.[version];
  if (!bundle || typeof bundle !== 'object') return {};
  return bundle as Record<string, unknown>;
}
